import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { normalizeCnpj } from '@shared/utils/open-finance-bank-map';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

/**
 * Represents a participant entry from the Open Finance Brazil directory API.
 */
interface DirectoryParticipant {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  Status: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  RegistrationNumber: string;
  // eslint-disable-next-line @typescript-eslint/naming-convention
  AuthorisationServers?: { CustomerFriendlyLogoUri?: string }[];
}

/**
 * Open Finance Brazil directory participants endpoint.
 */
const DIRECTORY_URL = 'https://data.directory.openbankingbrasil.org.br/participants';

/**
 * Fetches and caches bank logo URLs from the Open Finance Brazil directory.
 * The directory is loaded once per session on first call to load().
 */
@Injectable({ providedIn: 'root' })
export class OpenFinanceDirectoryService {
  /**
   * HTTP client for fetching the directory
   */
  private readonly http = inject(HttpClient);

  /**
   * In-memory cache mapping normalized CNPJ to logo URL
   */
  private readonly logoMap: Record<string, string> = {};

  /**
   * Guards against concurrent or duplicate fetch requests
   */
  private loading = false;

  /**
   * Internal subject tracking whether the directory has been loaded
   */
  private readonly loadedSubject = new BehaviorSubject(false);

  /**
   * Observable that emits true once the directory finishes loading
   */
  public readonly loaded$ = this.loadedSubject.asObservable();

  /**
   * Returns the logo URL for a given CNPJ from the cached directory data.
   * Returns null if the directory has not loaded yet or the CNPJ is not found.
   *
   * @param cnpj - Institution CNPJ in any format
   * @returns Logo URL string or null
   */
  public getLogoUrl(cnpj: string | null | undefined): string | null {
    if (!cnpj) return null;
    return this.logoMap[normalizeCnpj(cnpj)] ?? null;
  }

  /**
   * Triggers the directory fetch if not already loaded or in progress.
   * Subsequent calls while loading or after load are no-ops.
   */
  public load(): void {
    if (this.loadedSubject.value || this.loading) return;
    this.loading = true;
    this.http
      .get<DirectoryParticipant[]>(DIRECTORY_URL)
      .pipe(take(1))
      .subscribe({
        next: (participants) => {
          for (const p of participants) {
            if (p.Status !== 'Active') continue;
            const raw = p.RegistrationNumber ?? '';
            const cnpj = normalizeCnpj(raw);
            const logo = p.AuthorisationServers?.[0]?.CustomerFriendlyLogoUri;
            if (/\d/.test(raw) && logo) {
              this.logoMap[cnpj] = logo;
            }
          }
          this.loading = false;
          this.loadedSubject.next(true);
        },
        error: () => {
          this.loading = false;
        },
      });
  }
}
